import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowLeft, ShoppingBag } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useCart, cartLineKey } from '../contexts/CartContext';

const CartPage: React.FC = () => {
    const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
    const navigate = useNavigate();

    const formatPrice = (priceInEUR: number): string => {
        return `€${priceInEUR.toFixed(2)}`;
    };

    const handleUpdateQuantity = (key: string, currentQuantity: number, change: number) => {
        const newQuantity = Math.max(1, currentQuantity + change);
        updateQuantity(key, newQuantity);
    };

    const handleCheckout = () => {
        navigate('/checkout');
    };

    const handleRemoveItem = (key: string) => {
        removeFromCart(key);
    };

    const totalPriceFormatted = formatPrice(cart.total);

    return (
        <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
            <div className="container mx-auto py-6 px-4">
                <h1 className="text-xl sm:text-2xl font-bold mb-6">Количка</h1>

                {cart.items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 text-center">
                        <div className="flex justify-center mb-4">
                            <ShoppingBag className="h-12 sm:h-16 w-12 sm:w-16 text-gray-300" />
                        </div>
                        <h2 className="text-lg sm:text-xl font-medium mb-2">Вашата количка е празна</h2>
                        <p className="text-sm sm:text-base text-gray-600 mb-6">
                            Добавете продукти в количката, за да можете да направите поръчка.
                        </p>
                        <Link
                            to="/"
                            className="inline-block bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded"
                        >
                            Продължете с пазаруването
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Mobile View */}
                        <div className="lg:hidden">
                            <div className="space-y-4 mb-6">
                                {cart.items.map((item) => {
                                    const itemPrice = item.pricePromo !== null ? item.pricePromo : item.price;
                                    const itemTotal = itemPrice * item.quantity;
                                    
                                    const priceFormatted = formatPrice(item.price);
                                    const promoPriceFormatted = item.pricePromo ? formatPrice(item.pricePromo) : null;
                                    const totalFormatted = formatPrice(itemTotal);

                                    return (
                                        <div key={cartLineKey(item)} className="bg-white border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start gap-3">
                                                <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded">
                                                    {item.image ? (
                                                        <img
                                                            src={item.image}
                                                            alt={item.name}
                                                            className="h-16 w-16 object-contain"
                                                        />
                                                    ) : (
                                                        <div className="h-16 w-16 flex items-center justify-center text-gray-400 text-xs">
                                                            Няма изображение
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <Link
                                                        to={`/product/${item.slug ?? item.id}`}
                                                        className="text-sm font-medium text-gray-900 hover:text-green-600 break-words leading-5"
                                                    >
                                                        {item.name}
                                                    </Link>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Реф. № {item.referenceNumber}
                                                    </div>

                                                    <div className="mt-2">
                                                        {promoPriceFormatted && item.pricePromo !== null ? (
                                                            <div>
                                                                <span className="text-sm font-medium text-red-600">
                                                                    {promoPriceFormatted}
                                                                </span>
                                                                <span className="text-xs text-gray-500 line-through ml-2">
                                                                    {priceFormatted}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-sm font-medium">
                                                                {priceFormatted}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mt-3 pt-3 border-t gap-2">
                                                <div className="flex items-center border border-gray-300 rounded flex-shrink-0">
                                                    <button
                                                        onClick={() => handleUpdateQuantity(cartLineKey(item), item.quantity, -1)}
                                                        className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </button>
                                                    <span className="px-3 py-1 border-l border-r border-gray-300 min-w-[2rem] text-center">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => handleUpdateQuantity(cartLineKey(item), item.quantity, 1)}
                                                        className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                    </button>
                                                </div>

                                                <div className="text-right flex-shrink-0">
                                                    <div className="text-sm font-medium">
                                                        {totalFormatted}
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveItem(cartLineKey(item))}
                                                        className="text-xs text-red-600 hover:text-red-800 mt-1"
                                                    >
                                                        Премахни
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Mobile Summary */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                                <h2 className="text-base font-bold mb-3">Резюме на поръчката</h2>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Общо продукти:</span>
                                        <span>{cart.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Междинна сума:</span>
                                        <span>{totalPriceFormatted}</span>
                                    </div>
                                    <div className="flex justify-between font-bold text-base pt-2 border-t">
                                        <span>Общо:</span>
                                        <span>{totalPriceFormatted}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleCheckout}
                                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded font-medium mb-3"
                            >
                                Продължи към плащане
                            </button>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <Link to="/" className="flex items-center justify-center text-green-600 hover:underline text-sm">
                                    <ArrowLeft className="h-4 w-4 mr-1" />
                                    Продължете с пазаруването
                                </Link>
                                <button
                                    onClick={clearCart}
                                    className="text-red-500 hover:text-red-700 flex items-center justify-center text-sm"
                                >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Изчисти количката
                                </button>
                            </div>
                        </div>

                        {/* Desktop View */}
                        <div className="hidden lg:grid grid-cols-3 gap-6">
                            <div className="col-span-2">
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="w-full table-fixed">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="w-[45%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Продукт
                                                </th>
                                                <th className="w-[20%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Цена
                                                </th>
                                                <th className="w-[15%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Количество
                                                </th>
                                                <th className="w-[20%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Общо
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {cart.items.map((item) => {
                                                const itemPrice = item.pricePromo !== null ? item.pricePromo : item.price;
                                                const itemTotal = itemPrice * item.quantity;
                                                
                                                const priceFormatted = formatPrice(item.price);
                                                const promoPriceFormatted = item.pricePromo ? formatPrice(item.pricePromo) : null;
                                                const totalFormatted = formatPrice(itemTotal);

                                                return (
                                                    <tr key={cartLineKey(item)}>
                                                        <td className="px-4 py-4 w-[45%]">
                                                            <div className="flex items-start gap-3">
                                                                <div className="h-14 w-14 flex-shrink-0 bg-gray-100 rounded">
                                                                    {item.image ? (
                                                                        <img
                                                                            src={item.image}
                                                                            alt={item.name}
                                                                            className="h-14 w-14 object-contain"
                                                                        />
                                                                    ) : (
                                                                        <div className="h-14 w-14 flex items-center justify-center text-gray-400 text-xs">
                                                                            Няма изображение
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <Link
                                                                        to={`/product/${item.slug ?? item.id}`}
                                                                        className="text-sm font-medium text-gray-900 hover:text-green-600 break-words leading-5"
                                                                    >
                                                                        {item.name}
                                                                    </Link>
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Реф. № {item.referenceNumber}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleRemoveItem(cartLineKey(item))}
                                                                        className="mt-2 inline-flex items-center text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition-colors duration-200"
                                                                    >
                                                                        <Trash2 className="h-3 w-3 mr-1" />
                                                                        Премахни
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 w-[20%]">
                                                            {promoPriceFormatted && item.pricePromo !== null ? (
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-medium text-red-600">
                                                                        {promoPriceFormatted}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 line-through">
                                                                        {priceFormatted}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm font-medium block">
                                                                    {priceFormatted}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4 w-[15%]">
                                                            <div className="flex items-center border border-gray-300 rounded w-20">
                                                                <button
                                                                    onClick={() => handleUpdateQuantity(cartLineKey(item), item.quantity, -1)}
                                                                    className="px-1.5 py-1 text-gray-600 hover:bg-gray-100"
                                                                >
                                                                    <Minus className="h-3 w-3" />
                                                                </button>
                                                                <span className="flex-1 text-center py-1 text-sm border-l border-r border-gray-300">
                                                                    {item.quantity}
                                                                </span>
                                                                <button
                                                                    onClick={() => handleUpdateQuantity(cartLineKey(item), item.quantity, 1)}
                                                                    className="px-1.5 py-1 text-gray-600 hover:bg-gray-100"
                                                                >
                                                                    <Plus className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-4 w-[20%]">
                                                            <span className="text-sm font-medium">
                                                                {totalFormatted}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="flex justify-between mt-6">
                                    <Link to="/" className="flex items-center text-green-600 hover:underline">
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Продължете с пазаруването
                                    </Link>

                                    <button
                                        onClick={clearCart}
                                        className="text-red-500 hover:text-red-700 flex items-center"
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        Изчисти количката
                                    </button>
                                </div>
                            </div>

                            <div className="col-span-1">
                                <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-4">
                                    <h2 className="text-lg font-bold mb-4">Резюме на поръчката</h2>

                                    <div className="border-t border-gray-200 pt-4">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Общо продукти:</span>
                                            <span>{cart.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                                        </div>

                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Междинна сума:</span>
                                            <span className="text-sm">
                                                {totalPriceFormatted}
                                            </span>
                                        </div>

                                        <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-4 mt-4">
                                            <span>Общо:</span>
                                            <span className="text-base">
                                                {totalPriceFormatted}
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleCheckout}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded mt-6 font-medium"
                                    >
                                        Продължи към плащане
                                    </button>

                                    <div className="mt-4 text-xs text-gray-500 text-center">
                                        <p className="mt-2">Приемаме всички основни кредитни и дебитни карти.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default CartPage;